// @flow

import * as React from 'react';
import { ClickTracker } from '@ta/common.tracking';
import { unescaped } from '@ta/common.i18n';
import { Icon } from '@ta/styleguide.icon';
import type { NavLinkType } from '../types';

type Props = {|
  /** Data model for the link */
  link: NavLinkType,

  /** Extra classes set by parent. Not ideal but beats duplicating logic this component contains since this link can show in many places. */
  extraClasses?: string,

  /** Ref target function for setting an element as the target. Used as part of HoverChain and GeoPillFlyout. */
  setTarget: () => void,

  /** Show quick link icon */
  showQuickLinkIcon?: boolean,

  /** Custom classes the icon should have */
  quickLinkIconClasses?: string,

  /** Module name used in tracking */
  trackingModule?: string,

  /**
   * Calls e.preventDefault. Useful for preventing href from being followed.
   * E.g., originally used for popping the TargetedFlyout geopill on unscoped home.
   * If true we will force tracking via AJAX since page might not change.
   */
  preventDefault?: boolean,

  /** Custom label/html to be added on */
  extraLabel?: React.Node,

  /** Whether to render the link use <span /> or <a /> */
  nonLink?: boolean,

  /** optional additional actions to take on click */
  onClick?: (SyntheticEvent<*>) => mixed,
|};

/**
 * A top-level nav link.
 * The goal is that this can be shaped as a nav link, collapsed link (i.e., in the more menu), a quick link, or in the mobile sidebar.
 * Thus, in an effort to not duplicate logic (e.g., tracking, help center overlay), this link accepts classnames and has different display modes.
 */
const TopLevelNavLink = (props: Props) => {
  const HELP_CENTER_ID = 'HelpDesk';
  const TRAVEL_FEED = 'tourism';
  const isHelpCenter = props.link.id === HELP_CENTER_ID;
  const SCOPED_HOME = 'scoped-home';

  /**
   * For special links like the help center.
   */
  const clickHandler = (e: SyntheticEvent<*>) => {
    if (props.preventDefault) {
      e.preventDefault();
    }

    if (props.onClick) {
      props.onClick(e);
    }

    if (props.link.id === HELP_CENTER_ID) {
      e.preventDefault();
      // TODO: WP-270 includes creating the help center overlay in React
      global.require(
        ['trjs!brand/HelpCenterOverlay', 'trcss!src/build/required/help_center_overlay'],
        o => {
          o.open(e.target, '/uvpages/helpCenterOverlay.html');
        },
      );
    }

    if (props.link.id === TRAVEL_FEED || props.link.id === SCOPED_HOME) {
      e.preventDefault();
      // eslint-disable-next-line no-restricted-globals
      window.open(props.link.url, props.link.isNewWindow ? '_blank' : '_self');
    }
  };

  const innerLinkElement = (
    <React.Fragment>
      {props.showQuickLinkIcon && props.link.icon && (
        <Icon name={props.link.icon} className={props.quickLinkIconClasses} />
      )}
      {unescaped(props.link.text)}
      {props.extraLabel}
    </React.Fragment>
  );

  const linkElement = props.nonLink ? (
    <span ref={props.setTarget} className={props.extraClasses} onClick={clickHandler}>
      {innerLinkElement}
    </span>
  ) : (
    <a
      ref={props.setTarget}
      className={props.extraClasses}
      href={props.link.url}
      target={props.link.isNewWindow ? '_blank' : null}
      onClick={clickHandler}
    >
      {innerLinkElement}
    </a>
  );

  return (
    <ClickTracker
      module={props.trackingModule}
      action="click"
      forceAjax={isHelpCenter || props.preventDefault}
      context={props.link.trackingLabel}
    >
      {linkElement}
    </ClickTracker>
  );
};

TopLevelNavLink.defaultProps = {
  extraClasses: undefined,
  setTarget: () => {},
  showQuickLinkIcon: false,
  trackingModule: 'TopNav',
  quickLinkIconClasses: undefined,
  preventDefault: false,
  extraLabel: undefined,
  onClick: undefined,
};

export default TopLevelNavLink;
